import { useMemo, useRef } from 'react';
import { REGIONS, type Region } from '@photos/shared';
import { useSession } from '../state/session';

/**
 * Church, fellowship and service tags from the tag registry, for the profile pickers. These are fixed lists kept in
 * the database (nobody creates groups from the app).
 */
export function useProfileTags() {
  const { tagIndex } = useSession();
  return useMemo(() => {
    const of = (category: 'church' | 'fellowship' | 'service') =>
      [...tagIndex.registry.values()].filter((t) => t.category === category).sort((a, b) => a.label.localeCompare(b.label));
    return { churches: of('church'), fellowships: of('fellowship'), services: of('service') };
  }, [tagIndex]);
}

/** Church `<option>`s grouped by region (East, Central, West), each group sorted by name. */
export function ChurchOptions({ churches }: { churches: readonly { name: string; label: string; region?: Region }[] }) {
  const option = (c: { name: string; label: string }) => (
    <option key={c.name} value={c.name}>
      {c.label}
    </option>
  );
  return (
    <>
      {REGIONS.map((region) => {
        const inRegion = churches.filter((c) => c.region === region);
        return (
          inRegion.length > 0 && (
            <optgroup key={region} label={region}>
              {inRegion.map(option)}
            </optgroup>
          )
        );
      })}
      {churches.filter((c) => !c.region).map(option)}
    </>
  );
}

interface FellowshipSelectProps {
  value: string | null;
  onChange(fellowship: string | null): void;
  label: string;
  id?: string;
  className?: string;
}

/** "Fellowship group": at most one (Fathers, Mothers, Young Adults, Seniors…). */
export function FellowshipSelect({ value, onChange, label, id, className }: FellowshipSelectProps) {
  const { fellowships } = useProfileTags();
  return (
    <select id={id} className={className} aria-label={id ? undefined : label} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">None</option>
      {fellowships.map((f) => (
        <option key={f.name} value={f.name}>
          {f.label}
        </option>
      ))}
    </select>
  );
}

interface ServiceCheckboxesProps {
  options: { name: string; label: string }[];
  value: string[];
  onChange(services: string[]): void;
  label: string;
}

/**
 * "Service groups": any number (Choir, Translation, Media, AV, Church School…), as a row of labelled checkboxes
 * (not tag chips, which would look like search tags). Works without a session (request-access form).
 */
export function ServiceCheckboxes({ options, value, onChange, label }: ServiceCheckboxesProps) {
  // Read/write the latest value through a ref so quick successive clicks don't overwrite each other.
  const latest = useRef(value);
  latest.current = value;
  const toggle = (name: string, on: boolean) => {
    const cur = latest.current.filter((x) => x !== name);
    const next = on ? [...cur, name] : cur;
    latest.current = next;
    onChange(next);
  };
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map((g) => (
        <label key={g.name} className="toggle" style={{ padding: '4px 10px' }}>
          <input type="checkbox" checked={value.includes(g.name)} onChange={(e) => toggle(g.name, e.target.checked)} />
          {g.label}
        </label>
      ))}
    </div>
  );
}

/** Service groups from the tag registry (signed-in editors). */
export function ServicePicker(props: Omit<ServiceCheckboxesProps, 'options'>) {
  const { services } = useProfileTags();
  return <ServiceCheckboxes options={services} {...props} />;
}
